import { useState, useEffect, useCallback } from 'react';
import { formatDateTimeExact } from '@chikara/utils';
import { DASHBOARD_ITEMS_PER_PAGE, STATUS_TRANSACTION, TYPE_TRANSACTION } from '@/features/dashboard/constants/dashboard.constants';
import { accountService } from '@/features/card/services/accountService/accountService';
import { Transaction } from '@/features/card/types/account.types';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { isBasic } from '@/shared/config/permissions';
import { t } from '@/i18n';

interface UseDashboardMovementsReturn {
  data: Transaction[];
  total: number;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardMovements(customerUuid: string, page: number, role?: string, userUuid?: string): UseDashboardMovementsReturn {
  const { show, hide } = useSpinner();
  const [data, setData] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { checkGuard, resetGuard } = useAsyncGuard();

  const fetchMovements = useCallback(async () => {
    if (!customerUuid || !userUuid || !checkGuard()) return;
    show();
    setError(null);
    try {
      const genericFilters: Record<string, any>[] = [];
      const txs = await accountService.getAccountTransactions({
        ...(isBasic(role) && userUuid ? {
          user: {
            uuid: userUuid
          }
        } : {
          customer: {
            uuid: customerUuid
          }
        }),
        filter: {
          page_number: page,
          results_per_page: DASHBOARD_ITEMS_PER_PAGE,
          ...(genericFilters.length > 0 && { genericFilters })
        }
      });

      const tx = txs?.result?.tx?.map((tx: any) => {
        return {
          uuid: tx.uuid,
          amount: tx.amount,
          email: tx?.user?.email,
          movement: {
            description: tx.description || "No aplica",
            subcategory: `${tx?.card?.pan_masked?.slice(-4) || "----"} / Realizado por ${tx?.user?.fullname?.trim() || tx?.user?.email}`,
          },
          type: tx.type && TYPE_TRANSACTION[tx.type] || tx.type,
          status: tx.status,
          report: tx.status === 'settled' || tx.status === 'on_transit' || tx.status === 'approved' || tx.status === 'pending',
          statusName: tx.status && STATUS_TRANSACTION[tx.status] || tx.status,
          cdate: formatDateTimeExact(tx.cdate),
          tracking_key: tx.tracking_key || "",
          narrative: tx.remote_account.narrative || "",
          movement_mobile: tx.description || "No aplica",
          cdate_mobile: formatDateTimeExact(tx.cdate),
          pan_masked: tx?.card?.pan_masked?.slice(-4) || "----",
        }
      })
      setData(tx);
      setTotal(txs.result.total_documents);
    } catch {
      setError(t('dashboard.errors.generic'));
      resetGuard();
    } finally {
      hide();
    }
  }, [customerUuid, userUuid, role, page, show, hide, checkGuard, resetGuard]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  return {
    data,
    total,
    error,
    refetch: () => {
      resetGuard();
      return fetchMovements();
    }
  };
}
